import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const badgeVariants = cva(
  'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2',
  {
    variants: {
      variant: {
        default:
          'border-transparent bg-primary text-primary-foreground hover:bg-primary/80',
        secondary:
          'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80',
        destructive:
          'border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80',
        outline: 'text-foreground',
        // Status variants
        success: 'border-transparent bg-green-100 text-green-700',
        warning: 'border-transparent bg-yellow-100 text-yellow-700',
        error: 'border-transparent bg-red-100 text-red-700',
        info: 'border-transparent bg-blue-100 text-blue-700',
        // Custom status variants
        planned: 'border-transparent bg-slate-100 text-slate-700',
        released: 'border-transparent bg-blue-100 text-blue-700',
        kitting: 'border-transparent bg-amber-100 text-amber-700',
        in_progress: 'border-transparent bg-yellow-100 text-yellow-700',
        testing: 'border-transparent bg-purple-100 text-purple-700',
        completed: 'border-transparent bg-green-100 text-green-700',
        closed: 'border-transparent bg-gray-100 text-gray-700',
        cancelled: 'border-transparent bg-red-100 text-red-700',
        draft: 'border-transparent bg-gray-100 text-gray-600',
        active: 'border-transparent bg-green-100 text-green-700',
        archived: 'border-transparent bg-gray-100 text-gray-500',
        // Procurement
        make: 'border-transparent bg-green-100 text-green-700',
        buy: 'border-transparent bg-orange-100 text-orange-700',
        // Product types
        system: 'border-transparent bg-purple-100 text-purple-700',
        sub_assembly: 'border-transparent bg-blue-100 text-blue-700',
        raw_material: 'border-transparent bg-gray-100 text-gray-700',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return (
    <div className={cn(badgeVariants({ variant }), className)} {...props} />
  );
}

export { Badge, badgeVariants };
